import { useCallback, useSyncExternalStore } from "react";
import { SAMMLUNG_HAELT_STUNDEN, bedienerPflicht } from "@mrmachine/fach";
import { ATTRAPPE, hole, ersetze, loesche } from "./klient";
import { nachschlagen } from "./nachschlagen";
import { postenmenge } from "./postenmenge";
import { jeAuftragSchonGezaehlt } from "./jeauftrag";
import { preisFehlt } from "./preisfehlt";
import { zuschlaegeZusammenfuehren, zuschlagNetto } from "./zuschlagzusammenfuehren";
import { vorlagenstand } from "./vorlagestand";
import { gemeinsamerZeitraum } from "./leistungstermin";

export { gemeinsamerZeitraum, leistungstermin } from "./leistungstermin";

/* ══════════════════════════════════════════════════════════════════
   Die Merkliste — der Warenkorb der Preisauskunft.

   Luca, 17.09.2026, vor der fertigen Auskunft: *„Ich suche Schlepper und
   Mulde mit Fahrer für 2.720 €, und habe dieses X oben rechts. Ich will
   aber auch einen Button ‚Merken'. Dann ist es wie ein Warenkorb, und
   dann kann ich weitermachen und noch Preise für was anderes suchen.
   Unten rechts in der Ecke habe ich generell zwei Buttons: jetzt
   vermieten und Angebot erstellen."*

   Das ist der Weg vom Telefonat zum Auftrag, den es bis dahin nicht gab:
   Die Auskunft beantwortete eine Frage und vergaß sie sofort wieder. Wer
   drei Geräte durchrechnete, tippte sie danach in der Schnellerfassung
   noch einmal.

   **Sie lebt im Browser und sonst nirgends.** Ein halber Auftrag in der
   Datenbank stünde in jemandes Liste und müsste weggeräumt werden —
   dieselbe Überlegung wie beim Angebotsentwurf am Telefon
   (`masken/handy/entwurf.ts`) und in `AngebotBauen.tsx` („Der Entwurf
   entsteht erst mit der ersten Position").

   **Jeder Zugriff steht in try/catch.** `localStorage` wirft im privaten
   Fenster, bei gesperrten Website-Daten und wenn der Speicher voll ist.
   Eine Preisauskunft, die an einer Speicherausnahme scheitert, wäre
   schlimmer als eine ohne Merkliste — dann steht die Liste eben nur, bis
   der Reiter zugeht.

   **Der Betrag ist eine Anzeige, keine Zusage** (Regel 4). Eingefroren
   wird bei der Annahme, nicht beim Merken: Was hier steht, ist der Preis
   vom Augenblick der Auskunft. Der Abschluss holt ihn neu und sagt es,
   wenn sich etwas geändert hat.

   **Eingehängt wird sie über `useMerkliste()`.** Der Haken gibt die
   Posten, die Summe und die vier Handgriffe heraus; die Leiste unten
   rechts (`bausteine/Merkliste.tsx`) hängt in der Schale und zeigt sich
   von selbst, sobald etwas darinsteht.

   **Und sie hat zwei Türen** (Luca, 17.09.2026): *„Ich will mir die
   Kisten sowohl beim Verfügbarkeitscheck als auch bei der Preisauskunft
   merken können."* Die Preisauskunft merkt eine **beantwortete** Frage —
   mit Betrag, Miettagen und Zuschlägen. Der Verfügbarkeitscheck merkt
   eine **gestellte**: Typ und Zeitraum, sonst nichts. Darum nimmt
   `merken` beides an und füllt den Rest mit den Vorgaben; was fehlt,
   holt der Abschluss nach, und bis dahin steht an der Zeile „Preis
   offen" statt einer erfundenen Null.
   ══════════════════════════════════════════════════════════════════ */

const FACH = "mrmachine.merkliste";

/** Das zweite Fach: für wen die Sammlung ist. Der Grund steht weiter unten. */
const FACH_WER = "mrmachine.merkliste.wer";

/**
 * Das dritte Fach: **wann hier zuletzt etwas geschah** (Migration 0134).
 *
 * Seit die Sammlung am Menschen hängt, treffen beim Anmelden zwei Stände
 * aufeinander — der auf diesem Gerät und der auf dem Server. Ohne diesen
 * Zeitpunkt gäbe es keine Antwort auf „welcher ist der neuere", und der
 * Abgleich müsste raten. Er steht getrennt von den Posten, damit eine
 * Formatänderung an der Liste ihn nicht mitnimmt.
 */
const FACH_STAND = "mrmachine.merkliste.stand";

/**
 * Wie lange ein gemerkter Posten gilt.
 *
 * Zwölf Stunden, wie der Angebotsentwurf am Telefon. Ein Posten von
 * gestern ist kein Warenkorb, sondern ein Fund — und ein Fund, den man
 * versehentlich vermietet, ist ein Auftrag über den falschen Zeitraum.
 *
 * **Ausgeführt, weil die gestellte Frage dieselbe Frist hat**
 * (`daten/vermietstand.ts`, 17.09.2026). Eine Sammlung, die noch steht,
 * und eine Frage, die schon weg ist, wären zwei Alter für einen Vorgang.
 *
 * **Die Zahl steht seit dem 18.09.2026 in `@mrmachine/fach`**
 * (`SAMMLUNG_HAELT_STUNDEN`). Seit die Sammlung am Menschen hängt
 * (Migration 0134), misst auch der Server das Alter der Zeile daran —
 * und zwei Fassungen derselben Frist wären zwei Alter für eine Sammlung.
 */
export const HAELT_MS = SAMMLUNG_HAELT_STUNDEN * 60 * 60 * 1000;

/** Mehr passt auf keine Leiste, und wer mehr braucht, baut ein Angebot. */
const HOECHSTENS = 30;

/**
 * Ein Zuschlag, wie die Auskunft ihn gerechnet hat — und was der Mensch
 * daran geändert hat.
 *
 * **`netto` ist die gerechnete Zahl und bleibt es** (Regel 4): Sie kommt aus
 * der Preisfindung und wird hier nur mitgeschrieben. `aus` und `einzelpreis`
 * sind die beiden Eingriffe, die das Blatt am Posten erlaubt — abwählen und
 * einen anderen Satz zusagen. Beide gehen als eigene Zuschlagszeile hinaus und
 * schlagen dort die Regel desselben Codes („ein zugesagter Wert vor einem
 * gerechneten", `packages/fach/src/abrechnung.ts`). Genau so macht es die
 * Schnellerfassung heute; nur steht es dort in einem Feld statt an der Zeile.
 */
export type Merkzuschlag = {
  code: string;
  bezeichnung: string;
  netto: number;
  /** Abgewählt — die Zeile geht mit 0,00 hinaus und sagt, was entfällt. */
  aus?: boolean;
  /**
   * **Abgewählt vom Schalter „Kunde holt ab" und nicht von Hand** (0127).
   *
   * Der Unterschied entscheidet den Rückweg: Wer den Schalter wieder ausmacht,
   * bekommt die Anlieferung zurück — aber nur die, die der Schalter
   * weggenommen hat. Einen Zuschlag, den jemand selbst abgewählt hat, schaltet
   * er nicht ungefragt wieder an.
   */
  ausWegenAbholung?: boolean;
  /**
   * **Der Betrag ist erfunden** (`is_placeholder`, 0032/0036) — niemand hat
   * ihn entschieden. Steht an der Karte, weil eine Zahl, die niemand gesetzt
   * hat, nicht aussehen darf wie eine gesetzte.
   */
  platzhalter?: boolean;
  /** Dieser Zuschlag entfällt bei Selbstabholung — sagt der Stamm (0127). */
  faelltBeiAbholung?: boolean;
  /**
   * **Preis immer individuell** (Migration 0227, E1 — die Anlieferung). Im
   * Stamm steht kein Satz; `netto` ist 0, und solange kein `einzelpreis`
   * zugesagt ist, heißt die Zeile „Preis fehlt" (`preisFehlt`). „Jetzt
   * vermieten" geht dann nicht — ein Angebot schon.
   */
  preisIndividuell?: boolean;
  /**
   * **Einmal je Auftrag** (`scope = 'auftrag'`, die Anlieferung). Zwei Geräte
   * desselben Postens sind zwei Positionen, aber eine Anlieferung — eine
   * Zeile von Hand dafür geht nur einmal hinaus (Durchgang 03.10.2026, V-20).
   */
  jeAuftrag?: boolean;
  /**
   * Wie oft — aus der Preisfindung, wie `netto`, und mit jedem Nachrechnen
   * neu (08.10.2026). Sie zählt für den Betrag, sobald es von Hand einen
   * anderen Satz gibt, und geht dann als Menge der Zeile hinaus.
   */
  menge?: number;
  /** Der zugesagte Satz je Einheit. Fehlt er, gilt der gerechnete. */
  einzelpreis?: number;
};

/**
 * **Ein frisch gerechneter Zuschlag, wie die Sammlung ihn ablegt.**
 *
 * Aus der Preisauskunft und aus dem Bündel kommt dieselbe Zeile unter
 * denselben Namen; hier wird sie auf das eingedampft, was ein Posten trägt.
 * Mitgenommen werden auch die beiden Aussagen, die **keine** Beträge sind:
 * ob der Satz erfunden ist (0032/0036) und ob er bei Selbstabholung entfällt
 * (0127). Ohne sie wüsste die Karte nach dem ersten Nachrechnen nicht mehr,
 * welche Zeile der Schalter anfassen darf.
 */
export function alsMerkzuschlag(z: {
  code: string;
  bezeichnung: string;
  netto: number;
  menge?: number;
  einzelpreis?: number;
  platzhalter?: boolean;
  faelltBeiAbholung?: boolean;
  preisIndividuell?: boolean;
  jeAuftrag?: boolean;
}): Merkzuschlag {
  return {
    code: z.code,
    bezeichnung: z.bezeichnung,
    netto: z.netto,
    ...(z.menge === undefined ? {} : { menge: z.menge }),
    ...(z.platzhalter ? { platzhalter: true } : {}),
    ...(z.faelltBeiAbholung ? { faelltBeiAbholung: true } : {}),
    ...(z.preisIndividuell ? { preisIndividuell: true } : {}),
    ...(z.jeAuftrag ? { jeAuftrag: true } : {}),
  };
}

/**
 * **Fehlt an diesem Zuschlag der Preis?** (Migration 0227, E1) — ein Zuschlag
 * mit „Preis immer individuell", der nicht abgewählt ist und für den noch
 * niemand einen Satz genannt hat. Dann steht er als „Preis fehlt" da, und
 * „Jetzt vermieten" hält an. Die Regel steht in `daten/preisfehlt.ts`.
 */
export { preisFehlt };

/** Die Zuschläge eines Postens, denen der Preis fehlt — für die Meldung beim Abschluss. */
export function fehlendePreise(p: Merkposten): Merkzuschlag[] {
  return p.zuschlaege.filter(preisFehlt);
}

/* **Die Zusammenführung und das Netto je Zuschlag** stehen seit dem
   08.10.2026 in `daten/zuschlagzusammenfuehren.ts` — rein, damit sie sich
   ohne Browser prüfen lassen (`test/zuschlagmenge.test.ts`). Dort steht
   auch, warum die Menge dem Zeitraum folgt und nicht dem alten Stand. */
export { zuschlaegeZusammenfuehren, zuschlagNetto } from "./zuschlagzusammenfuehren";

/**
 * **Welche Art Zeile das ist** (18.09.2026).
 *
 * Bis dahin trug die Sammlung nur Mietposten, und genau das war der eine
 * echte Verlust gegenüber der alten Schnellerfassung: Ein Anruf, bei dem
 * neben dem Bagger noch eine Mulchleistung und eine Palette Verschleißteile
 * dazukommen, ging nur über die alte Maske
 * (`docs/38-vermietmodul.md`, „Was bis zum Umschalten noch fehlt", 1).
 *
 * **Dieselben fünf Wörter wie überall** (`bausteine/Positionen.tsx`,
 * `NeueArt`; `Schnellposition.art` in `daten/routen.ts`; `art` an der
 * Position drüben). Ein sechstes Wort für dieselbe Sache wäre die Stelle, an
 * der zwei Listen auseinanderlaufen.
 */
export type Merkart = "miete" | "leistung" | "verkauf" | "zuschlag" | "text";

/** Trägt diese Zeile ein Gerät — Zeitraum, Lage, Belegung? */
export function istMiete(p: { art?: Merkart }): boolean {
  return (p.art ?? "miete") === "miete";
}


/* ══════════════ „Das letzte Mal war ja gut" (18.09.2026) ══════════════

   Der häufigste Anruf im Innendienst: *„Den gleichen Kran wie beim letzten
   Mal."* Bis heute konnte ihn nur die alte Schnellerfassung beantworten
   (`uebergabe/2026-09-16-epos-luecken.md`, Lücke 3); ihn abzuschalten hieße,
   die Lücke wieder aufzumachen (`docs/38-vermietmodul.md`, „Was bis zum
   Umschalten noch fehlt", Punkt 1).

   **Die Herkunft hängt am Posten, nicht an der Sammlung.** Zwei Gründe: Ein
   Anruf darf zwei alte Vorgänge zusammenziehen — „den Kran von August und
   die Rüttelplatte von Mai" —, und der Satz von damals ist ohnehin je Zeile
   ein anderer. Eine Sammlung mit einer Herkunft wäre für das eine zu eng und
   für das andere zu grob.

   **Der Satz von damals wird nie gerechnet** (Regel 4). Er steht daneben,
   damit auffällt, dass sich etwas geändert hat; gerechnet wird aus dem
   heutigen Stamm und dem neuen Zeitraum. */

/** Woraus ein Posten entstanden ist — ein früherer Vorgang. */
export type Merkvorlage = {
  /** Die Belegnummer des alten Vorgangs. */
  nr: string;
  /** `24.08.` — wann er geschrieben wurde. */
  datum: string;
  /**
   * Der eingefrorene Satz **je Einheit** von damals. `null` heißt: Der alte
   * Vorgang trug dafür keinen Betrag.
   */
  satz: number | null;
  /**
   * Im Stamm steht heute **kein** Satz — ein Zuschlag nach Prozent, eine
   * Zeile ohne Katalog. Dann IST die Zahl am Posten die von damals, und das
   * muss dabeistehen: Ein Preis von vorletztem Jahr, der aussieht wie einer
   * von heute, geht sonst ungeprüft zum Kunden.
   */
  ohneStamm?: boolean;
  /**
   * Die Maschine, die es damals war — Kennung, oder leer.
   *
   * **Ein Wunsch, keine Zusage** (Regel 2): Verkauft wird der Typ. Sie steht
   * hier und nicht in `maschine`, weil der Haken „wieder dasselbe Stück" von
   * sich aus aus ist — erst wer ihn setzt, schreibt sie nach `maschine`.
   */
  maschine?: string;
  /**
   * **Wie viele Einheiten der Satz damals galt** — bei der Tagesstaffel die
   * Miettage von damals, mit ihrer Einheit (Prüfrunde Runde 10, „Neu
   * gesehen"). Ein Satz für einen Miettag und einer für sieben liegen auf
   * verschiedenen Stufen der Staffel; „ein Preis ist heute ein anderer" wäre
   * bei jeder anderen Dauer wahr und sagte nichts.
   */
  einheiten?: number;
  einheitKey?: string;
};

/** Ein gemerkter Posten — genau das, was die Auskunft beantwortet hat. */
export type Merkposten = {
  /** Die Kennung der Zeile, nicht die des Typs: derselbe Typ darf zweimal
   *  darinstehen, mit zwei Zeiträumen. */
  id: string;
  /**
   * **Was für eine Zeile das ist.** Vorgabe `miete` — ein alter Stand im
   * Browser kannte das Feld nicht und trug ausschließlich Mieten.
   *
   * Nur eine `miete` hat Typ, Zeitraum, Lage und Belegung. Leistung, Ware,
   * Zuschlag und Freitext sind Zeilen auf dem Beleg und sonst nichts: Was
   * sie nicht haben, steht an ihrer Karte auch nicht.
   */
  art: Merkart;
  /** Die Kennung des Maschinentyps. Nur an einer `miete` gefüllt. */
  typId: string;
  /** Der Name des Maschinentyps (Regel 2) — ohne Ausstattung. */
  typ: string;
  /**
   * Der Name, der auf dem Beleg steht — mit Ausstattung, wenn es eine gibt
   * (`bausteine/Ausstattung.tsx`, `mitAusstattung`).
   *
   * Er wird beim Merken zusammengesetzt und nicht beim Anlegen: Ein Kran für
   * 105 und einer für 120 dürfen drüben nicht gleich heißen, und wer die
   * beiden Teile erst im Abschluss wieder zusammenfügt, hat den Punkt
   * zweimal im Code stehen.
   */
  bezeichnung: string;
  gruppe: string;
  /** Die Ausstattung — `""` heißt Grundpreis. */
  varianteId: string;
  /** Ihre Beschriftung, leer wenn der Typ keine hat. */
  variante: string;
  von: string;
  bis: string;
  /** Die gezählten Miettage der Auskunft — nicht die Kalendertage. */
  miettage: number;
  bedient: boolean;
  /** Wie viele Stück. Vorgabe 1; jedes Stück wird einzeln disponiert. */
  menge: number;
  /** Die Miete je Stück, wie sie beim Merken gerechnet war. */
  netto: number;
  /** Die vorgewählten Zuschläge je Stück — Summe und Einzelteile. */
  zuschlaegeNetto: number;
  zuschlaege: Merkzuschlag[];
  /**
   * **Auf diesen Posten schlägt bewusst nichts auf** (Migration 0144).
   *
   * Der Baumaschinenanhänger „mit Baumaschine" kostet 15,00 einmal je Miete
   * — hart kalkuliert, und ein Rücknahme-Check obendrauf macht daraus eine
   * andere Zahl (Luca, 18.09.2026). Es steht hier neben `zuschlaege` und
   * nicht statt ihrer: Eine leere Liste sieht genauso aus wie ein Posten,
   * für den noch niemand gerechnet hat. Kommt aus der Preisauskunft
   * (`Preisauskunft.zuschlagsfrei`) und wird nicht hier entschieden.
   */
  zuschlagsfrei?: boolean;
  /**
   * Stand beim Merken ein Betrag da?
   *
   * `false` heißt: Der Posten kommt aus dem Verfügbarkeitscheck, wo nach
   * einem Gerät gefragt wird und nicht nach einem Preis. Eine Null wäre an
   * dieser Stelle eine Aussage — und zwar eine falsche. Der Abschluss holt
   * den Betrag und setzt das Feld dabei.
   */
  gerechnet: boolean;
  /**
   * **Der Zeitraum ist neu, der Betrag noch der alte** (Systemprüfung A-06,
   * 24.09.2026).
   *
   * Lisa: *„Der angezeigte Preis war der für 5 Tage, obwohl nur 1 Tag
   * angefragt war."* Wer den Zeitraum an der Karte ändert, bekam neu
   * gerechnet — wer ihn im Abschluss „für alle Posten" änderte, nicht: Der
   * Posten trug danach den neuen Zeitraum und den Betrag des alten. Das Feld
   * setzt `postenAendern` bei jedem neuen Zeitraum an einem gerechneten
   * Posten; wer ihn als Nächstes zeichnet (Karte, Blatt, Abschluss), rechnet
   * nach und nimmt es wieder weg.
   */
  zeitraumNeu?: boolean;
  /**
   * **Dieser Posten hat seinen eigenen Zeitraum** (08.10.2026). Gesetzt,
   * wenn jemand ihn einzeln auf einen anderen Zeitraum stellt als den, der
   * für alle gilt; weg mit „wie alle". Ändert sich der gemeinsame Zeitraum,
   * folgt er nicht (`daten/eigenerzeitraum.ts`).
   */
  eigenerZeitraum?: boolean;

  /* ── Was das Blatt am Posten dazulegt (17.09.2026) ──────────────────
     Bis hierher trug ein Posten eine beantwortete Frage. Mit dem
     Vermietmodul (docs/38-vermietmodul.md, Schritt 2) trägt er, was die
     Schnellerfassung an einer Zeile kann — sonst wäre der Regelweg ärmer
     als vorher. Alles davon ist **freiwillig**: Ein Posten aus der
     Preisauskunft hat nichts davon, und der Abschluss kommt damit aus. */

  /** Die Kennung der gewünschten Maschine. Leer heißt: eine freie nehmen. */
  maschine?: string;
  /**
   * Der Mensch hat bestätigt, dass er ein Gerät nimmt, das ein anderes Angebot
   * für seine Bindefrist reserviert hat (18.09.2026). Ohne diese Bestätigung
   * weist die API das Buchen mit `belegung.reserviert` ab.
   */
  reservierungUebergehen?: boolean;
  /** Die Kennung der Person, die bedient. Leer heißt: noch niemand. */
  bediener?: string;
  /** Die Zubehörstücke, die mit hinausgehen — Ressourcen-UUIDs. */
  zubehoer?: string[];
  /**
   * **Was die Anbauteile mit eigenem Tarif kosten** — je Stück dieses Postens
   * (Migration 0078, nachgezogen am 18.09.2026).
   *
   * Seit 0078 legt die API für jedes gebundene Stück, dessen Typ im Stamm
   * einen Tarif hat, eine **eigene Zeile** unter der Miete an. Die Sammlung
   * rechnete sie nicht mit: Der Auftrag stimmte, die Summe davor war zu klein
   * — genau die stille Abweichung, die eine Zahl nach dem Klick größer werden
   * lässt (docs/38-vermietmodul.md, Punkt 3).
   *
   * **Gerechnet wird er nicht hier, sondern geholt** (Regel 4): Der Baustein
   * `bausteine/Anbauteile.tsx` fragt je Anbauteiltyp `GET /preis` mit dem
   * Zeitraum des Trägers und der Kondition des Kunden — dieselbe
   * Preisfindung, die drüben die Zeile schreibt — und legt das Ergebnis hier
   * ab. Fehlt das Feld, gibt es kein Anbauteil mit eigenem Tarif.
   */
  anbauNetto?: number;
  /**
   * Ein von Hand zugesagter Nettosatz **je Einheit** für die Miete.
   *
   * Nicht die Summe: Bei der Tagesstaffel steht der Satz je Tag, und die
   * Menge sind die Miettage. Wer 500 statt 575 zusagt, meint 500 je Tag —
   * dieselbe Unterscheidung wie in der Schnellerfassung, wo sie einmal
   * falsch herum stand.
   */
  einzelpreis?: number;
  /** Wie viele Einheiten der Tarif gezählt hat — für „Satz × Menge" am Blatt. */
  einheiten?: number;
  /** Der i18n-Schlüssel der Einheit, wie ihn die Preisfindung nennt. */
  einheitKey?: string;
  /** Was das Gerät an Bedienung verlangt (Migration 0083, aufgelöst). */
  bedienungspflicht?: "ohne_person" | "nur_mit_person" | "wahlweise";

  /* ── Selbstabholung (18.09.2026, Migration 0127) ────────────────────
     Luca: *„Gibt es aber ja auch den Fall, dass Maschinen abgeholt werden
     vom Kunden … Da muss man hier in der Merkliste dann direkt einen
     Schnellwahlbutton haben: ‚Kunde holt ab'."* */

  /** Passt dieses Gerät hinter ein Auto? Sonst steht der Schalter gar nicht. */
  abholbar?: boolean;
  /** Der Schalter steht an: Wir liefern nicht, der Kunde kommt. */
  selbstabholung?: boolean;
  /**
   * Der Anhänger, der bei Selbstabholung vorgeschlagen wird — mit seiner
   * Ausstattung, weil erst sie den Preis „mit Baumaschine" trägt (0102).
   *
   * **Er wird ein eigener Posten und keine Zeile an diesem** (Regel 8): Ein
   * Anhänger steht zur selben Zeit nur einmal auf dem Hof und braucht seine
   * eigene Belegung.
   */
  anhaenger?: { typId: string; typ: string; varianteId: string; variante: string };
  /**
   * **Der Auflieger, der hinter diese Zugmaschine gehört** (Migration 0072).
   *
   * Dieselbe Sorte Auskunft wie `anhaenger` und aus demselben Grund am
   * Posten: Der Schalter in der Sammlung soll ohne zweite Abfrage dastehen.
   * Ein **Vorschlag**, keine Bedingung — eine Zugmaschine ohne Auflieger ist
   * kein Fehler.
   */
  gespann?: { typId: string; typ: string };
  /** Der Schalter steht an: Der Auflieger geht mit. */
  mitAuflieger?: boolean;
  /**
   * **Dieser Posten ist der Anhänger zu jenem.**
   *
   * Er trägt selbst keinen Schalter, und wer den Schalter drüben ausmacht,
   * nimmt ihn wieder heraus. Ein Vorschlag, kein Riegel (0072): Wer ihn von
   * Hand entfernt, hat ihn entfernt.
   */
  wegen?: string;
  /**
   * **Aus welchem Anlass er dazukam** — Selbstabholung (0127) oder Gespann
   * (0072).
   *
   * Beide hängen über `wegen` an demselben Posten, und beide dürfen zugleich
   * anstehen: Eine Zugmaschine, die der Kunde selbst holt, braucht keinen
   * Transportanhänger und trotzdem ihren Auflieger. Ohne dieses Wort suchte
   * jeder Schalter den Posten des anderen und nähme ihn beim Ausschalten mit.
   */
  wegenArt?: "abholung" | "gespann";

  /* ── Was eine weitere Zeile trägt (18.09.2026) ──────────────────────
     Leistung, Ware und Zuschlag kommen aus einem Katalog; der Freitext aus
     keinem. Die Kennung geht als `leistungsart_id`, `ware_id` oder
     `zuschlag_code` an dieselbe Route, die auch die Schnellerfassung
     benutzt hat (`Schnellposition`) — von dort kommen Bezeichnung, Einheit
     und Satz aus dem Stamm. Ohne Kennung ist es eine Zeile von Hand
     („Hydraulikschlauch, Ersatz"), und dann trägt sie ihren Namen selbst. */

  /** Die Leistungsart aus dem Katalog — nur an `art: "leistung"`. */
  leistungsartId?: string;
  /**
   * **Der gewählte Grad** (Preiskategorie, etwa der Verschmutzungsgrad) einer
   * Leistung — P8-05. Nur, wenn ein Mensch gewählt hat; die Vorgabe oder der
   * einzige Grad gilt ohne Eintrag (`daten/leistungssatz.ts`). Geht als
   * `kategorie_id` an die Position.
   */
  kategorieId?: string;
  /**
   * **Die Menge folgt aus dem Aufmaß** (V-22, Durchgang 03.10.2026) — eine
   * Leistung mit Ortsaufnahme-Pflicht (`needs_site_survey`). Sie wird mit
   * Menge 0 gemerkt, nicht mit 1: Eine Eins sähe aus wie ein gemessener
   * Quadratmeter, und die Annahme friert sie als Ist ein (Regel 3). Dieselbe
   * Regel wie in der API (`routen/vorgaenge.ts`, „Menge folgt aus dem Aufmaß").
   */
  aufmass?: boolean;
  /** Der Warenartikel aus dem Katalog — nur an `art: "verkauf"`. */
  wareId?: string;
  /** Der Code aus dem Zuschlagsstamm — nur an `art: "zuschlag"`. */
  zuschlagCode?: string;

  /** Woraus dieser Posten entstanden ist, wenn er aus einem alten Vorgang kommt. */
  vorlage?: Merkvorlage;

  /**
   * **Für wen gemerkt wurde** — Kundennummer und Name zum Zeitpunkt des
   * Merkens (Lisa, 24.09.2026). Die Sammlung hat oben einen Kunden, aber ein
   * Anruf kann liegen bleiben („ich melde mich in einer Stunde"), und der
   * nächste Anrufer darf ihn nicht mitbekommen. Leer heißt: gemerkt, bevor
   * ein Kunde gewählt war — dann gehört der Posten dem, der jetzt oben steht.
   */
  kunde?: string;
  kundeName?: string;

  /** Wann gemerkt wurde. Was älter ist als `HAELT_MS`, fällt weg. */
  gemerktAm: number;
};

/**
 * Gehört der Posten zum Kunden, der oben steht? Ein Posten ohne Kunden gehört
 * jedem — er wurde gemerkt, bevor jemand gewählt war.
 */
export function gehoertZu(p: Merkposten, nr: string): boolean {
  return !p.kunde || p.kunde === nr;
}

/* Der Satz von damals und die Marke darüber stehen seit der Prüfrunde Runde 10
   in `daten/vorlagestand.ts` — rein, damit sie sich ohne Browser prüfen lassen. */
export { damalsWert, gerechneterSatz, vorlagenstand } from "./vorlagestand";
/** Der Satz je Einheit, der für diesen Posten gilt — von Hand vor gerechnet. */
export function mietsatz(p: Merkposten): number {
  if (p.einzelpreis === undefined) return p.netto;
  return Math.round(p.einzelpreis * (p.einheiten ?? 1) * 100) / 100;
}

/**
 * Was die Zuschläge eines Postens kosten — **je Stück** die gewöhnlichen,
 * **einmal** die „je Auftrag" (V-20-Nebenbefund, 03.10.2026). `nicht` nennt
 * die Codes „je Auftrag", die schon an einem anderen Posten zählen
 * (`jeAuftragSchonGezaehlt`).
 */
export function zuschlagsteile(
  zuschlaege: readonly Merkzuschlag[],
  nicht: ReadonlySet<string> = new Set(),
): { jeStueck: number; einmal: number } {
  let jeStueck = 0;
  let einmal = 0;
  for (const z of zuschlaege) {
    if (!z.jeAuftrag) jeStueck += zuschlagNetto(z);
    else if (!nicht.has(z.code)) einmal += zuschlagNetto(z);
  }
  return { jeStueck, einmal };
}

/**
 * Was ein Posten mit seiner Menge kostet — Miete und Zuschläge.
 *
 * `nicht`: die Codes „je Auftrag", die schon an einem anderen Posten derselben
 * Liste zählen. Ohne Angabe zählt der Posten sie selbst — einmal, nicht je
 * Stück: Zwei Rüttler auf einem Posten bekommen eine Anlieferung.
 */
export function postenSumme(p: Merkposten, nicht?: ReadonlySet<string>): number {
  /* Eine Textzeile hat keinen Betrag — das ist ihr ganzer Zweck. Sie mit
     0,00 durchrechnen zu lassen ginge auch, stünde aber als „0,00" an der
     Karte, und eine Null sieht aus wie ein Preis. */
  if (p.art === "text") return 0;
  const teile = p.zuschlaege.length
    ? zuschlagsteile(p.zuschlaege, nicht)
    : { jeStueck: p.zuschlaegeNetto, einmal: 0 };
  /* **Die Anbauteile mit eigenem Tarif zählen mit** (0078, 18.09.2026). Sie
     werden drüben eigene Zeilen unter der Miete — eine je Träger, also
     einmal je Stück dieser Menge. Wer sie hier wegließe, zeigte bis zum
     Klick einen zu kleinen Betrag. */
  return Math.round(
    ((mietsatz(p) + teile.jeStueck + (p.anbauNetto ?? 0)) * p.menge + teile.einmal) * 100,
  ) / 100;
}

/** Je Posten der Liste: welche Codes „je Auftrag" er nicht mehr zählt. */
export function schonGezaehltJePosten(posten: readonly Merkposten[]): Map<Merkposten, Set<string>> {
  return jeAuftragSchonGezaehlt(posten.filter((p) => p.gerechnet), (p) => p.zuschlaege);
}

/**
 * Die Summe der Liste. Netto, wie jede Zahl in der Auskunft.
 *
 * Ein Posten ohne Betrag zählt mit null mit — und die Leiste sagt daneben,
 * wie viele das sind. Eine Summe, die eine offene Zeile verschweigt, ist
 * kleiner als die Wahrheit, und das ist die falsche Richtung.
 */
export function merklistenSumme(posten: Merkposten[]): number {
  /* **Die Anlieferung einmal, nicht je Posten** (V-20-Nebenbefund): wie der
     Server (`fach.zuschlaegeZusammenfassen`) und die AB. */
  const schon = schonGezaehltJePosten(posten);
  return Math.round(
    posten.filter((p) => p.gerechnet).reduce((s, p) => s + postenSumme(p, schon.get(p)), 0) * 100,
  ) / 100;
}

/* ══════════════ Der Speicher ══════════════ */

const zahl = (wert: unknown, vorgabe = 0): number => {
  const n = typeof wert === "number" ? wert : Number(wert);
  return Number.isFinite(n) ? n : vorgabe;
};
const txt = (wert: unknown): string => (typeof wert === "string" ? wert : "");

/** Was aus dem Speicher kommt, ist fremd — es wird gelesen, nicht geglaubt. */
function alsPosten(roh: unknown): Merkposten | null {
  if (!roh || typeof roh !== "object") return null;
  const x = roh as Record<string, unknown>;
  const von = txt(x["von"]);
  const bis = txt(x["bis"]);
  const typId = txt(x["typId"]);
  /* **Ein alter Stand kannte das Feld nicht** und trug ausschließlich
     Mieten. Was hier nicht als eins der fünf Wörter dasteht, ist eine
     Miete — und die braucht dann auch Typ und Zeitraum. */
  const roheArt = txt(x["art"]);
  const art: Merkart = roheArt === "leistung" || roheArt === "verkauf"
    || roheArt === "zuschlag" || roheArt === "text"
    ? roheArt
    : "miete";
  if (art === "miete" && (!typId || !von || !bis)) return null;
  /* Eine Zeile ohne Gerät ist ihre Bezeichnung. Ohne sie stünde sie namenlos
     in der Sammlung und würde drüben abgewiesen. */
  if (art !== "miete" && !txt(x["bezeichnung"])) return null;
  const zuschlaege = Array.isArray(x["zuschlaege"])
    ? (x["zuschlaege"] as unknown[])
      .map((z) => (z && typeof z === "object" ? (z as Record<string, unknown>) : null))
      .filter((z): z is Record<string, unknown> => z !== null)
      .map((z) => ({
        code: txt(z["code"]),
        bezeichnung: txt(z["bezeichnung"]),
        netto: zahl(z["netto"]),
        ...(z["aus"] === true ? { aus: true as const } : {}),
        ...(z["ausWegenAbholung"] === true ? { ausWegenAbholung: true as const } : {}),
        ...(z["platzhalter"] === true ? { platzhalter: true as const } : {}),
        ...(z["faelltBeiAbholung"] === true ? { faelltBeiAbholung: true as const } : {}),
        ...(z["preisIndividuell"] === true ? { preisIndividuell: true as const } : {}),
        ...(z["jeAuftrag"] === true ? { jeAuftrag: true as const } : {}),
        ...(z["menge"] === undefined ? {} : { menge: zahl(z["menge"], 1) }),
        ...(z["einzelpreis"] === undefined ? {} : { einzelpreis: zahl(z["einzelpreis"]) }),
      }))
    : [];
  const liste = (wert: unknown): string[] =>
    Array.isArray(wert) ? (wert as unknown[]).map(txt).filter(Boolean) : [];
  const pflicht = txt(x["bedienungspflicht"]);
  /* Ein Vorschlag ohne beide Kennungen ist keiner — ein Posten ohne Typ
     ließe sich nicht anlegen, und einer ohne Ausstattung träfe den Preis
     für den Anhänger, den jemand ohne Maschine mietet (0102). */
  const roheAnhaenger = x["anhaenger"] && typeof x["anhaenger"] === "object"
    ? (x["anhaenger"] as Record<string, unknown>)
    : null;
  const anhaenger = roheAnhaenger && txt(roheAnhaenger["typId"]) && txt(roheAnhaenger["varianteId"])
    ? {
      typId: txt(roheAnhaenger["typId"]),
      typ: txt(roheAnhaenger["typ"]),
      varianteId: txt(roheAnhaenger["varianteId"]),
      variante: txt(roheAnhaenger["variante"]),
    }
    : null;
  /* Ein Vorschlag ohne Kennung ist keiner — dieselbe Prüfung wie beim
     Anhänger einen Absatz höher. */
  const roheGespann = x["gespann"] && typeof x["gespann"] === "object"
    ? (x["gespann"] as Record<string, unknown>)
    : null;
  const gespann = roheGespann && txt(roheGespann["typId"])
    ? { typId: txt(roheGespann["typId"]), typ: txt(roheGespann["typ"]) }
    : null;
  const roheWegenArt = txt(x["wegenArt"]);
  const wegenArt = roheWegenArt === "abholung" || roheWegenArt === "gespann"
    ? roheWegenArt
    : null;
  /* Eine Herkunft ohne Nummer ist keine — sie soll an der Karte stehen
     („wiederholt aus A-2026-0042"), und ein leerer Satz sagt nichts. Der
     Satz von damals darf dagegen fehlen: Nicht jede alte Zeile trug einen. */
  const roheVorlage = x["vorlage"] && typeof x["vorlage"] === "object"
    ? (x["vorlage"] as Record<string, unknown>)
    : null;
  const vorlage: Merkvorlage | null = roheVorlage && txt(roheVorlage["nr"])
    ? {
      nr: txt(roheVorlage["nr"]),
      datum: txt(roheVorlage["datum"]),
      satz: roheVorlage["satz"] === null || roheVorlage["satz"] === undefined
        ? null
        : zahl(roheVorlage["satz"]),
      ...(roheVorlage["ohneStamm"] === true ? { ohneStamm: true as const } : {}),
      ...(txt(roheVorlage["maschine"]) ? { maschine: txt(roheVorlage["maschine"]) } : {}),
      ...(zahl(roheVorlage["einheiten"]) > 0 ? { einheiten: zahl(roheVorlage["einheiten"]) } : {}),
      ...(txt(roheVorlage["einheitKey"]) ? { einheitKey: txt(roheVorlage["einheitKey"]) } : {}),
    }
    : null;
  return {
    id: txt(x["id"]) || `${typId}:${von}:${bis}`,
    art,
    typId,
    typ: txt(x["typ"]),
    bezeichnung: txt(x["bezeichnung"]) || txt(x["typ"]),
    gruppe: txt(x["gruppe"]),
    varianteId: txt(x["varianteId"]),
    variante: txt(x["variante"]),
    von,
    bis,
    miettage: Math.max(0, Math.round(zahl(x["miettage"]))),
    bedient: x["bedient"] === true,
    /* Eine Textzeile hat keine Menge — sie steht als Satz auf dem Beleg.
       Überall sonst ist mindestens eins gemeint. */
    menge: postenmenge(art, x["menge"] === undefined ? undefined : zahl(x["menge"], 1), x["aufmass"] === true),
    netto: zahl(x["netto"]),
    zuschlaegeNetto: zahl(x["zuschlaegeNetto"]),
    zuschlaege,
    // Ein alter Stand ohne das Feld trug seine Zuschläge (0144).
    ...(x["zuschlagsfrei"] === true ? { zuschlagsfrei: true } : {}),
    // Ein alter Stand ohne das Feld trug immer einen Betrag.
    gerechnet: x["gerechnet"] !== false,
    ...(x["zeitraumNeu"] === true ? { zeitraumNeu: true } : {}),
    ...(x["eigenerZeitraum"] === true ? { eigenerZeitraum: true } : {}),
    ...(txt(x["maschine"]) ? { maschine: txt(x["maschine"]) } : {}),
    ...(x["reservierungUebergehen"] === true ? { reservierungUebergehen: true } : {}),
    ...(txt(x["bediener"]) ? { bediener: txt(x["bediener"]) } : {}),
    ...(liste(x["zubehoer"]).length ? { zubehoer: liste(x["zubehoer"]) } : {}),
    ...(x["anbauNetto"] === undefined ? {} : { anbauNetto: zahl(x["anbauNetto"]) }),
    ...(x["einzelpreis"] === undefined ? {} : { einzelpreis: zahl(x["einzelpreis"]) }),
    ...(x["einheiten"] === undefined ? {} : { einheiten: zahl(x["einheiten"], 1) }),
    ...(txt(x["einheitKey"]) ? { einheitKey: txt(x["einheitKey"]) } : {}),
    ...(pflicht === "ohne_person" || pflicht === "nur_mit_person" || pflicht === "wahlweise"
      ? { bedienungspflicht: pflicht }
      : {}),
    ...(x["abholbar"] === true ? { abholbar: true } : {}),
    ...(x["selbstabholung"] === true ? { selbstabholung: true } : {}),
    ...(anhaenger ? { anhaenger } : {}),
    ...(gespann ? { gespann } : {}),
    ...(x["mitAuflieger"] === true ? { mitAuflieger: true } : {}),
    ...(txt(x["wegen"]) ? { wegen: txt(x["wegen"]) } : {}),
    ...(wegenArt ? { wegenArt } : {}),
    ...(txt(x["leistungsartId"]) ? { leistungsartId: txt(x["leistungsartId"]) } : {}),
    ...(txt(x["kategorieId"]) && art === "leistung" ? { kategorieId: txt(x["kategorieId"]) } : {}),
    ...(x["aufmass"] === true && art === "leistung" ? { aufmass: true } : {}),
    ...(txt(x["wareId"]) ? { wareId: txt(x["wareId"]) } : {}),
    ...(txt(x["zuschlagCode"]) ? { zuschlagCode: txt(x["zuschlagCode"]) } : {}),
    ...(vorlage ? { vorlage } : {}),
    ...(txt(x["kunde"]) ? { kunde: txt(x["kunde"]) } : {}),
    ...(txt(x["kundeName"]) ? { kundeName: txt(x["kundeName"]) } : {}),
    gemerktAm: zahl(x["gemerktAm"], Date.now()),
  };
}

function ausSpeicher(): Merkposten[] {
  try {
    const roh = window.localStorage.getItem(FACH);
    if (!roh) return [];
    const gelesen = JSON.parse(roh) as unknown;
    if (!Array.isArray(gelesen)) return [];
    const grenze = Date.now() - HAELT_MS;
    return gelesen
      .map(alsPosten)
      .filter((p): p is Merkposten => p !== null && p.gemerktAm >= grenze)
      .slice(0, HOECHSTENS);
  } catch {
    return [];
  }
}

/* **Ein Stand im Speicher der Seite, nicht nur im Speicher des Browsers.**
   Die Leiste unten rechts und die Preisauskunft sind zwei Bäume; ohne
   gemeinsamen Stand würde die Leiste erst beim nächsten Zeichnen merken,
   dass etwas dazugekommen ist. `useSyncExternalStore` ist dafür der Weg,
   den React selbst vorsieht — kein Kontext, den jede Maske umschließen
   müsste, und kein Ereignis, das irgendwo hängen bleibt. */
let stand: Merkposten[] | null = null;
const hoerer = new Set<() => void>();

function jetzt(): Merkposten[] {
  if (stand === null) stand = ausSpeicher();
  return stand;
}

function setzen(neu: Merkposten[]): void {
  stand = neu.slice(0, HOECHSTENS);
  try {
    window.localStorage.setItem(FACH, JSON.stringify(stand));
  } catch {
    /* Kein Platz, kein Recht, keine Ablage — die Liste steht trotzdem,
       solange der Reiter offen ist. */
  }
  beruehrt();
  hoerer.forEach((ruf) => ruf());
}

function anmelden(ruf: () => void): () => void {
  hoerer.add(ruf);
  /* Ein zweiter Reiter ist derselbe Betrieb: Wer dort etwas merkt, soll es
     hier sehen, ohne neu zu laden. `storage` feuert nur in den ANDEREN
     Reitern — der eigene bekommt seinen Stand über `setzen`. */
  const fremd = (e: StorageEvent) => {
    if (e.key !== null && e.key !== FACH && e.key !== FACH_WER) return;
    stand = ausSpeicher();
    // Wer im anderen Reiter den Kunden gewählt hat, hat ihn für denselben
    // Anruf gewählt — beide Fächer gehören zu einer Sammlung.
    werStand = werAusSpeicher();
    hoerer.forEach((x) => x());
  };
  window.addEventListener("storage", fremd);
  return () => {
    hoerer.delete(ruf);
    window.removeEventListener("storage", fremd);
  };
}

/** Auf dem Server gibt es keinen Speicher — und diese Anwendung läuft
 *  ohnehin nur im Browser. Der Rückweg hält `useSyncExternalStore` ruhig. */
const LEER: Merkposten[] = [];

/* ══════════════ Für wen, und wohin ══════════════

   Luca, 17.09.2026, nach dem Durchklicken: *„Für ‚Wen' muss einfach oben bei
   Gerät mit hin, also zwischen Gerät und Zeitraum, dann Kunde, Baustelle, und
   das sind optionale Felder, die spätestens erforderlich sind, wenn ich auf
   ‚Jetzt vermieten' oder ‚Angebot erstellen' klicke."*

   **Damit gehört der Kunde in die Sammlung und nicht in eine Maske.** Sie ist
   der Auftragsentwurf, und ein Entwurf hat einen Empfänger; gefragt wird er
   oben in der Vermietfläche, gebraucht wird er unten im Abschluss, und
   dazwischen liegt ein Deckblatt und womöglich ein Maskenwechsel. Stünde er im
   Gedächtnis der Fläche, wäre er weg, sobald jemand ins Brett schaut — genau
   der Grund, aus dem die Posten hier stehen und nicht dort.

   **Gespeichert wird das Wenige, was die Zeile zeigt**, nicht der ganze
   Kundensatz: Nummer, Name, Art. Alles andere holt die Maske frisch — ein
   Kunde, der seit gestern gesperrt ist, soll nicht aus dem Browserspeicher
   wieder ungesperrt hervorkommen.

   **Ein eigenes Fach neben dem der Posten.** Wer einen alten Stand liest,
   findet die Liste, wie sie war; wer keinen Kunden gewählt hat, hat kein Fach.
   Eine Formatänderung an der Liste hätte alles darin weggeworfen. */

/** Für wen der Entwurf ist — so viel, wie die Zeile zeigt. */
export type Merkempfaenger = {
  /** Die Kundennummer aus der Liste — `10001`, nicht die UUID. */
  nr: string;
  name: string;
  art: "kunde" | "interessent" | "vermieter";
  /** Der Einsatzort, so wie er beim Kunden heißt. Leer heißt: keiner. */
  ort: string;
  /**
   * **Bestellnummer/Kommission und Hinweis für den Hof** (08.10.2026) —
   * Kopfangaben des Entwurfs wie Kunde und Ort, deshalb hier und nicht im
   * Gedächtnis des Abschlusses: Wer ihn schließt und weitersucht, findet sie
   * wieder. Sie gehören zum Anruf — ein anderer Kunde fängt leer an. Sie
   * liegen nur im Browser; der Server führt an der Sammlung Kunde und Ort.
   */
  bestellnummer: string;
  hofhinweis: string;
};

export const OHNE_EMPFAENGER: Merkempfaenger = {
  nr: "", name: "", art: "kunde", ort: "", bestellnummer: "", hofhinweis: "",
};

function werAusSpeicher(): Merkempfaenger {
  try {
    const roh = window.localStorage.getItem(FACH_WER);
    if (!roh) return OHNE_EMPFAENGER;
    const x = JSON.parse(roh) as Record<string, unknown>;
    const art = txt(x["art"]);
    return {
      nr: txt(x["nr"]),
      name: txt(x["name"]),
      art: art === "interessent" || art === "vermieter" ? art : "kunde",
      ort: txt(x["ort"]),
      bestellnummer: txt(x["bestellnummer"]),
      hofhinweis: txt(x["hofhinweis"]),
    };
  } catch {
    return OHNE_EMPFAENGER;
  }
}

let werStand: Merkempfaenger | null = null;

function werJetzt(): Merkempfaenger {
  if (werStand === null) werStand = werAusSpeicher();
  return werStand;
}

/**
 * Kunde und Einsatzort setzen — beides freiwillig.
 *
 * Ohne Kunden rechnet die Trefferliste den Listenpreis, mit Kunden seine
 * Kondition. Pflicht wird er erst am Abschluss, und dort steht er dann schon
 * da (Regel 11: was gesetzt werden kann, wird nicht zweimal gefragt).
 */
export function empfaengerSetzen(teil: Partial<Merkempfaenger>): void {
  const neu = { ...werJetzt(), ...teil };
  /* Ein anderer Kunde heißt ein anderer Einsatzort — seine Baustelle nimmt der
     nächste nicht mit. Das gilt nur beim WECHSEL: Wer den Ort setzt, ohne den
     Kunden anzufassen, behält ihn. */
  const alt = werJetzt();
  const gewechselt = teil.nr !== undefined && teil.nr !== alt.nr;
  /* **Beim Wechsel behält, was schon gemerkt war, seinen Kunden** (Lisa,
     24.09.2026): Die Schnellvermietung für A liegt, weil A zurückrufen will;
     dazwischen ruft B an. Ohne diese Zeile ginge As Rüttler mit in Bs
     Auftrag — und mit As Kondition gerechnet. */
  if (gewechselt && alt.nr && jetzt().some((p) => !p.kunde)) {
    setzen(jetzt().map((p) => (p.kunde ? p : { ...p, kunde: alt.nr, kundeName: alt.name })));
  }
  /* Bestellnummer und Hofhinweis gehören zum Anruf — der nächste Kunde
     fängt leer an, wie beim Ort. */
  werStand = gewechselt
    ? {
        ...neu,
        ...(teil.ort === undefined ? { ort: "" } : {}),
        ...(teil.bestellnummer === undefined ? { bestellnummer: "" } : {}),
        ...(teil.hofhinweis === undefined ? { hofhinweis: "" } : {}),
      }
    : neu;
  try {
    if (werStand.nr || werStand.ort || werStand.bestellnummer || werStand.hofhinweis) {
      window.localStorage.setItem(FACH_WER, JSON.stringify(werStand));
    } else {
      window.localStorage.removeItem(FACH_WER);
    }
  } catch {
    /* Wie bei der Liste: Was nicht abgelegt werden kann, steht trotzdem,
       solange der Reiter offen ist. */
  }
  beruehrt();
  hoerer.forEach((ruf) => ruf());
}

/* ══════════════ Die Handgriffe ══════════════ */

/**
 * Was ein neuer Posten mitbringt.
 *
 * **Pflicht sind drei Dinge: welcher Typ, ab wann, bis wann.** Alles andere
 * hat eine Vorgabe, damit auch der Verfügbarkeitscheck merken kann — dort
 * ist nach einem Gerät gefragt und nicht nach einem Preis (Luca,
 * 17.09.2026). Wer den Betrag hat, gibt ihn mit; wer ihn nicht hat, lässt
 * ihn weg, und der Posten steht als „Preis offen" in der Liste.
 *
 * `tage` ist dasselbe wie `miettage` und steht daneben, weil die andere
 * Seite ihre Dauer so nennt. Beides gesetzt: `miettage` gilt.
 */
export type Merkwunsch = {
  /**
   * **Welche Art Zeile** — ohne Angabe eine Miete (18.09.2026).
   *
   * Nur eine Miete braucht Typ und Zeitraum. Leistung, Ware, Zuschlag und
   * Freitext brauchen eine Bezeichnung und sonst nichts.
   */
  art?: Merkart;
  /** Die Kennung des Maschinentyps. Pflicht an einer Miete, sonst leer. */
  typId?: string;
  /** Der Name des Typs. Ohne ihn steht die Zeile ohne Namen da. */
  typ?: string;
  /** Der Name auf dem Beleg — mit Ausstattung. Fehlt er, gilt `typ`. */
  bezeichnung?: string;
  gruppe?: string;
  varianteId?: string;
  variante?: string;
  von?: string;
  bis?: string;
  /** Die gezählten Miettage, wenn sie schon feststehen. */
  miettage?: number;
  /** Dasselbe unter dem Namen, den der Verfügbarkeitscheck benutzt. */
  tage?: number;
  bedient?: boolean;
  menge?: number;
  /** Die Miete je Stück. Fehlt sie, ist der Preis offen. */
  netto?: number;
  zuschlaegeNetto?: number;
  zuschlaege?: Merkzuschlag[];
  /** Auf diesen Preis schlägt nichts auf (Migration 0144). */
  zuschlagsfrei?: boolean;
  /** Wie viele Einheiten der Tarif gezählt hat, und wie sie heißen. */
  einheiten?: number;
  einheitKey?: string;
  /** Was das Gerät an Bedienung verlangt — die Vermietfläche weiß es aus der Auskunft. */
  bedienungspflicht?: "ohne_person" | "nur_mit_person" | "wahlweise";
  /** Ob der Kunde selbst abholen kann, und was dann mitgeht (Migration 0127). */
  abholbar?: boolean;
  anhaenger?: { typId: string; typ: string; varianteId: string; variante: string };
  /** Der Auflieger, der hinter diese Zugmaschine gehört (Migration 0072). */
  gespann?: { typId: string; typ: string };
  /** Dieser Posten hängt an jenem — gesetzt von einem Schalter, nicht von Hand. */
  wegen?: string;
  wegenArt?: "abholung" | "gespann";
  /** Woher eine weitere Zeile kommt — leer heißt: von Hand getippt. */
  leistungsartId?: string;
  /** Die Leistung wird erst vor Ort gemessen — Menge 0 statt 1 (V-22). */
  aufmass?: boolean;
  wareId?: string;
  zuschlagCode?: string;
  /** Aus welchem alten Vorgang diese Zeile wiederholt wird (18.09.2026). */
  vorlage?: Merkvorlage;
};

function kennung(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Einen Posten merken — und dieselbe Frage nicht zweimal.
 *
 * Wer zweimal auf „Merken" drückt, hat nicht zwei Schlepper gemeint: Typ,
 * Ausstattung, Zeitraum und Bediener zusammen sind die Frage, und dieselbe
 * Frage steht einmal in der Liste. Die Menge daneben ist der Weg zum
 * zweiten Stück — sie ist sichtbar und änderbar, ein stiller zweiter
 * Eintrag wäre es nicht.
 */
/**
 * **Ein zweites „Merken" sagt, was es getan hat** (V-10, Durchgang
 * 03.10.2026). Dieselbe Frage steht einmal in der Liste (unten, `gleich`) —
 * das ist richtig, aber der Ruf danach war derselbe wie beim ersten Mal, und
 * Doris drückte dreimal. Wer einen Posten zurückbekommt, der schon da war,
 * fragt hier nach und sagt den Weg zum zweiten Stück.
 */
const zusammengelegt = new WeakSet<Merkposten>();
export function warSchonGemerkt(p: Merkposten): boolean {
  return zusammengelegt.has(p);
}

export function merken(wunsch: Merkwunsch): Merkposten {
  const typ = wunsch.typ ?? "";
  const art: Merkart = wunsch.art ?? "miete";
  /* Eine Leistung ohne eigenen Tag bekommt den Zeitraum, den alle Mieten
     teilen (P10-02) — „+ Leistung" unter der Sammlung fragt keinen. */
  const termin = art === "leistung" && !wunsch.von ? gemeinsamerZeitraum(jetzt()) : {};
  const gefuellt = {
    art,
    typId: wunsch.typId ?? "",
    typ,
    bezeichnung: wunsch.bezeichnung ?? typ,
    gruppe: wunsch.gruppe ?? "",
    varianteId: wunsch.varianteId ?? "",
    variante: wunsch.variante ?? "",
    von: wunsch.von ?? termin.von ?? "",
    bis: wunsch.bis ?? termin.bis ?? "",
    miettage: Math.max(0, Math.round(wunsch.miettage ?? wunsch.tage ?? 0)),
    bedient: wunsch.bedient === true,
    menge: postenmenge(art, wunsch.menge, wunsch.aufmass === true),
    netto: wunsch.netto ?? 0,
    zuschlaegeNetto: wunsch.zuschlaegeNetto ?? 0,
    zuschlaege: wunsch.zuschlaege ?? [],
    ...(wunsch.zuschlagsfrei ? { zuschlagsfrei: true as const } : {}),
    gerechnet: wunsch.netto !== undefined,
    ...(wunsch.einheiten === undefined ? {} : { einheiten: wunsch.einheiten }),
    ...(wunsch.einheitKey ? { einheitKey: wunsch.einheitKey } : {}),
    ...(wunsch.bedienungspflicht ? { bedienungspflicht: wunsch.bedienungspflicht } : {}),
    ...(wunsch.abholbar ? { abholbar: true } : {}),
    ...(wunsch.anhaenger ? { anhaenger: wunsch.anhaenger } : {}),
    ...(wunsch.gespann ? { gespann: wunsch.gespann } : {}),
    ...(wunsch.wegen ? { wegen: wunsch.wegen } : {}),
    ...(wunsch.wegenArt ? { wegenArt: wunsch.wegenArt } : {}),
    ...(wunsch.leistungsartId ? { leistungsartId: wunsch.leistungsartId } : {}),
    ...(wunsch.aufmass && art === "leistung" ? { aufmass: true } : {}),
    ...(wunsch.wareId ? { wareId: wunsch.wareId } : {}),
    ...(wunsch.zuschlagCode ? { zuschlagCode: wunsch.zuschlagCode } : {}),
    ...(wunsch.vorlage ? { vorlage: wunsch.vorlage } : {}),
    ...(werJetzt().nr ? { kunde: werJetzt().nr, kundeName: werJetzt().name } : {}),
  };

  /* **Eine weitere Zeile wird nie zusammengelegt.** Zweimal „Mulchen" am
     selben Anruf sind zwei Einsätze auf zwei Baustellen und nicht derselbe
     Wunsch zweimal gestellt — die Zusammenlegung unten gilt dem Gerät, das
     zweimal gemerkt wird, weil jemand die Liste noch einmal durchgeht. */
  if (art !== "miete") {
    const eigen: Merkposten = { ...gefuellt, id: kennung(), gemerktAm: Date.now() };
    setzen([...jetzt(), eigen]);
    return eigen;
  }

  const gleich = (p: Merkposten) =>
    p.art === "miete"
    && p.typId === gefuellt.typId
    && p.varianteId === gefuellt.varianteId
    && p.von === gefuellt.von
    && p.bis === gefuellt.bis
    && p.bedient === gefuellt.bedient
    /* Derselbe Rüttler für einen anderen Kunden ist ein anderer Wunsch. */
    && (p.kunde ?? "") === (gefuellt.kunde ?? "");

  const vorhanden = jetzt().find(gleich);
  if (vorhanden) {
    /* **Dieselbe Frage steht einmal in der Liste.** Wer zweimal auf „Merken"
       drückt, hat nicht zwei Schlepper gemeint; die Menge daneben ist der
       sichtbare Weg zum zweiten Stück.

       Aufgefrischt wird nur, was der Wunsch WEISS: Ein Posten, den die
       Preisauskunft mit Betrag abgelegt hat, verliert ihn nicht, wenn der
       Verfügbarkeitscheck denselben Typ noch einmal merkt. */
    const erneuert: Merkposten = {
      ...vorhanden,
      ...(typ ? { typ } : {}),
      ...(gefuellt.bezeichnung ? { bezeichnung: gefuellt.bezeichnung } : {}),
      ...(gefuellt.miettage ? { miettage: gefuellt.miettage } : {}),
      ...(gefuellt.gerechnet
        ? {
          netto: gefuellt.netto,
          zuschlaegeNetto: gefuellt.zuschlaegeNetto,
          /* Was von Hand daran geändert wurde, bleibt stehen: Wer einen
             Zuschlag abgewählt hat und dieselbe Frage noch einmal stellt,
             hat ihn nicht wieder angewählt. */
          zuschlaege: zuschlaegeZusammenfuehren(vorhanden.zuschlaege, gefuellt.zuschlaege),
          zuschlagsfrei: wunsch.zuschlagsfrei === true,
          gerechnet: true,
        }
        : {}),
      ...(wunsch.einheiten === undefined ? {} : { einheiten: wunsch.einheiten }),
      ...(wunsch.einheitKey ? { einheitKey: wunsch.einheitKey } : {}),
      ...(wunsch.bedienungspflicht ? { bedienungspflicht: wunsch.bedienungspflicht } : {}),
      ...(wunsch.abholbar === undefined ? {} : { abholbar: wunsch.abholbar }),
      ...(wunsch.anhaenger ? { anhaenger: wunsch.anhaenger } : {}),
      /* **Eine Wiederholung setzt die Herkunft auch an einem Posten, der
         schon dastand.** Wer erst den Rüttler merkt und dann den alten
         Vorgang wiederholt, in dem derselbe Rüttler stand, hat denselben
         Posten zweimal gemeint — und den Satz von damals will er sehen. Der
         Preis bleibt dabei stehen: `gerechnet` steht schon, und die Vorlage
         bringt keinen mit (Regel 4). */
      ...(wunsch.vorlage ? { vorlage: wunsch.vorlage } : {}),
      gemerktAm: Date.now(),
    };
    setzen(jetzt().map((p) => (p.id === vorhanden.id ? erneuert : p)));
    zusammengelegt.add(erneuert);
    return erneuert;
  }

  const neu: Merkposten = { ...gefuellt, id: kennung(), gemerktAm: Date.now() };
  setzen([...jetzt(), neu]);
  return neu;
}

/* ══════════════ Nur merken, was frei ist ══════════════

   Luca, 19.09.2026: *„Wenn ich eine Maschine merken will, die nicht
   verfügbar ist, kann ich das trotzdem — bis zum Klick auf Vermieten oder
   Angebot, dann kommt erst die Fehlermeldung. Es muss eine Fehlermeldung
   kommen, sobald man das Ding merken will, das darf ja nicht gehen.
   Richtiges Popup, sowohl auf Handy als auf Desktop."*

   **Dieselbe Frage wie im Abschluss**, eine Stelle früher: `GET
   /nachschlagen` für diesen Typ und dieses Fenster, und die Regel aus
   `MerklisteAbschluss.tsx` (`befundVon`) — genug frei heißt `frei ≥ menge`.
   Zwei Regeln an zwei Stellen liefen auseinander, und dann wäre gemerkt,
   was der Abschluss ablehnt.

   **Was schon in der Sammlung steht, zählt mit.** Ist ein Kran frei und
   liegt er schon einmal darin (etwa ohne Bediener), ist ein zweiter
   Posten desselben Typs im selben Fenster ein zweiter Kran — und den gibt
   es nicht. Derselbe Posten noch einmal gemerkt ist dagegen keiner mehr:
   `merken` legt ihn zusammen, und seine Menge bleibt.

   **Ohne Antwort keine Sperre.** Kommt die Lage nicht (kein Netz, der
   Server hustet), wird gemerkt: Eine Sammlung, die offline nichts mehr
   annimmt, wäre schlimmer als ein Posten, den der Abschluss danach noch
   einmal prüft. Das bleibt die zweite Linie, nicht mehr die erste. */

/** Warum ein Posten nicht in die Sammlung darf — für das Fenster, das es sagt. */
export type Merksperre = {
  /** Der Name auf dem Beleg — mit Ausstattung. */
  name: string;
  von: string;
  bis: string;
  /** Wie viele Stücke dieses Typs die Sammlung dann bräuchte. */
  braucht: number;
  /** Davon schon in der Sammlung, in anderen Posten desselben Typs. */
  schon: number;
  frei: number;
  bestand: number;
  /** Ab wann wieder eins frei ist — wenn die Lage es weiß. */
  freiAb: string | null;
  /**
   * **Nicht die Lage, sondern die Art** (Systemprüfung Befund 44,
   * 25.09.2026): ein Anbauteil, das nur an seinem Trägergerät hinausgeht —
   * die Schaufel ohne Radlader. Frei sein kann es trotzdem; gemerkt wird es
   * nicht, weil das Speichern es abweisen würde (`position.nur_mit_traeger`).
   */
  nurMitTraeger?: true;
};

/** Überschneiden sich zwei Fenster `JJJJ-MM-TT`? Ein leeres überschneidet nichts. */
function ueberlappt(a: { von: string; bis: string }, b: { von: string; bis: string }): boolean {
  if (!a.von || !a.bis || !b.von || !b.bis) return false;
  return a.von <= b.bis && b.von <= a.bis;
}

/** Dieselbe Frage wie in `merken` — dann wird zusammengelegt, nicht dazugelegt. */
function dieselbeFrage(p: Merkposten, w: Merkwunsch): boolean {
  return p.art === "miete"
    && p.typId === (w.typId ?? "")
    && p.varianteId === (w.varianteId ?? "")
    && p.von === (w.von ?? "")
    && p.bis === (w.bis ?? "")
    && p.bedient === (w.bedient === true);
}

/**
 * **Darf dieser Wunsch in die Sammlung?** `null` heißt ja.
 *
 * Nur eine Miete mit Typ und Zeitraum wird gefragt — alles andere ist eine
 * Zeile auf dem Beleg und hat keine Lage.
 */
export async function merksperreFinden(
  wunsch: Merkwunsch,
  /** Was vor diesem Wunsch schon dazukommt — für mehrere auf einmal. */
  dazu: readonly Merkwunsch[] = [],
): Promise<Merksperre | null> {
  if ((wunsch.art ?? "miete") !== "miete") return null;
  const typId = wunsch.typId ?? "";
  const von = wunsch.von ?? "";
  const bis = wunsch.bis ?? "";
  if (!typId || !von || !bis) return null;

  const fenster = { von, bis };
  const vorhanden = jetzt().find((p) => dieselbeFrage(p, wunsch));
  const eigen = vorhanden ? vorhanden.menge : Math.max(1, Math.round(wunsch.menge ?? 1));
  const schon = andereStuecke(typId, fenster, (p) => dieselbeFrage(p, wunsch))
    + dazu
      .filter((w) => (w.art ?? "miete") === "miete" && w.typId === typId
        && ueberlappt({ von: w.von ?? "", bis: w.bis ?? "" }, fenster))
      .reduce((summe, w) => summe + Math.max(1, Math.round(w.menge ?? 1)), 0);
  return lageFragen({ typId, von, bis, eigen, schon, name: wunsch.bezeichnung || wunsch.typ || "" });
}

/** Wie viele Stücke dieses Typs andere Posten im Fenster schon halten. */
function andereStuecke(
  typId: string,
  fenster: { von: string; bis: string },
  ausser: (p: Merkposten) => boolean,
): number {
  return jetzt()
    .filter((p) => istMiete(p) && p.typId === typId && !ausser(p) && ueberlappt(p, fenster))
    .reduce((summe, p) => summe + p.menge, 0);
}

/**
 * **Die eine Frage an den Server** — dieselbe, die der Abschluss stellt
 * (`GET /nachschlagen`), und dieselbe Regel (`frei ≥ gebraucht`).
 */
async function lageFragen(frage: {
  typId: string; von: string; bis: string; eigen: number; schon: number; name: string;
}): Promise<Merksperre | null> {
  let lage: Awaited<ReturnType<typeof nachschlagen>>["zeilen"][number] | undefined;
  try {
    const antwort = await nachschlagen({
      wort: "", gruppe: null, von: frage.von, bis: frage.bis, typen: [frage.typId],
    });
    lage = antwort.zeilen.find((z) => z.typId === frage.typId);
  } catch {
    return null;
  }
  if (!lage) return null;

  const braucht = frage.eigen + frage.schon;
  /* **Die Schaufel allein geht nicht — und das steht beim Merken da**
     (Systemprüfung Befund 44, Lisa, 25.09.2026: *„Die Meldung, dass die
     Schaufel nicht allein hinausgeht, kommt erst ganz am Ende beim
     Speichern — nicht schon beim Merken."*). Im Angebot stand ein solches
     Teil seit Befund 10 gar nicht mehr zur Wahl; in der Vermietfläche, im
     Nachschlagen und in der Wiederholung ließ es sich merken. Die Frage
     steht hier, weil jeder dieser Wege hier vorbeikommt — dieselbe Antwort,
     die die Lage ohnehin bringt, keine zweite Abfrage. Seinen Weg hinaus
     hat es im Blatt seines Trägers, als Anbauteil. */
  if (lage.anbaupflichtig) {
    return {
      name: frage.name || lage.typ,
      von: frage.von,
      bis: frage.bis,
      braucht,
      schon: frage.schon,
      frei: lage.frei,
      bestand: lage.bestand,
      freiAb: lage.freiAb,
      nurMitTraeger: true,
    };
  }
  /* **Was ein Angebot hält, darf in die Sammlung** (Systemprüfung B-08,
     24.09.2026). `frei` zählt Reserviertes nicht mit — richtig für die
     Auskunft, falsch für diese Sperre: Lisa wollte das eine Gerät merken,
     das ein Angebot für seine Bindefrist hält, und bekam „nichts frei",
     obwohl der Zeitraum passte. Damit war der Weg aus B-08 — „trotzdem
     nehmen" im Blatt zum Posten — gar nicht zu erreichen. Gemerkt wird es
     jetzt; ob es genommen wird, entscheidet ein Mensch im Blatt, und ohne
     diese Bestätigung weist das Buchen ab (`belegung.reserviert`). */
  if (lage.bestand > 0 && lage.frei + lage.reserviert >= braucht) return null;
  return {
    name: frage.name || lage.typ,
    von: frage.von,
    bis: frage.bis,
    braucht,
    schon: frage.schon,
    frei: lage.frei,
    bestand: lage.bestand,
    freiAb: lage.freiAb,
  };
}

/**
 * **Einen Posten in der Sammlung ändern — wenn es dann noch frei ist.**
 *
 * „+1 Stück" und ein anderer Zeitraum sind dieselbe Frage wie das Merken:
 * Wer den Rüttler von einem auf zwei Stück stellt, merkt ein zweites Gerät.
 * Gibt es das nicht, bleibt der Posten, wie er war, und das Fenster sagt es.
 *
 * **Gefragt wird nur, was mehr verlangt**: mehr Stücke oder ein anderer
 * Zeitraum. Weniger Stücke gehen immer — sie machen etwas frei.
 */
export async function postenAendernWennFrei(id: string, teil: Teilposten): Promise<boolean> {
  const alt = jetzt().find((p) => p.id === id);
  if (!alt || !istMiete(alt)) {
    postenAendern(id, teil);
    return true;
  }
  const menge = teil.menge ?? alt.menge;
  const von = teil.von ?? alt.von;
  const bis = teil.bis ?? alt.bis;
  const verlangtMehr = menge > alt.menge || von !== alt.von || bis !== alt.bis;
  if (verlangtMehr && alt.typId && von && bis) {
    const sperre = await lageFragen({
      typId: alt.typId, von, bis,
      eigen: menge,
      schon: andereStuecke(alt.typId, { von, bis }, (p) => p.id === id),
      name: alt.bezeichnung || alt.typ,
    });
    if (sperre) {
      merksperreZeigen([sperre]);
      return false;
    }
  }
  postenAendern(id, teil);
  return true;
}

/* ── Das Fenster, das es sagt ────────────────────────────────────────
   Ein Zustand für die ganze Oberfläche: Gemerkt wird an sechs Stellen,
   gezeigt wird an einer (`bausteine/Merksperre.tsx`, in der Schale). */

let sperren: Merksperre[] = [];
const sperrenHoerer = new Set<() => void>();

function sperrenMelden(): void {
  sperrenHoerer.forEach((ruf) => ruf());
}

/** Das Fenster öffnen — mit einem oder mehreren Posten, die nicht gingen. */
export function merksperreZeigen(liste: Merksperre[]): void {
  sperren = liste;
  sperrenMelden();
}

export function merksperreSchliessen(): void {
  sperren = [];
  sperrenMelden();
}

const KEINE_SPERRE: Merksperre[] = [];

export function useMerksperre(): Merksperre[] {
  return useSyncExternalStore(
    (ruf) => { sperrenHoerer.add(ruf); return () => { sperrenHoerer.delete(ruf); }; },
    () => sperren,
    () => KEINE_SPERRE,
  );
}

/**
 * **Merken, wenn es frei ist** — sonst das Fenster, und nichts wird gemerkt.
 *
 * Gibt den Posten zurück, oder `null`, wenn es nicht ging. Wer danach „steht
 * in der Sammlung" meldet, fragt vorher danach.
 */
export async function merkenWennFrei(wunsch: Merkwunsch): Promise<Merkposten | null> {
  const sperre = await merksperreFinden(wunsch);
  if (sperre) {
    merksperreZeigen([sperre]);
    return null;
  }
  return merken(wunsch);
}

/**
 * **Darf dieser Wunsch in die Sammlung?** — nur prüfen, nicht merken.
 *
 * Für die Schalter an einem Posten, die erst etwas umstellen und dann einen
 * zweiten Posten dazulegen (Auflieger, Anhänger): Geht der zweite nicht,
 * bleibt auch der Schalter, wie er war. Zeigt das Fenster selbst.
 */
export async function merkbar(wunsch: Merkwunsch): Promise<boolean> {
  const sperre = await merksperreFinden(wunsch);
  if (sperre) merksperreZeigen([sperre]);
  return sperre === null;
}

/**
 * **Fehlt an diesem Posten der Mensch, der ihn fahren muss?**
 *
 * Luca, 17.09.2026: *„Ich habe den Kran genommen, aber ich wurde beim direkten
 * Vermieten gar nicht dazu aufgefordert, den Fahrer auszuwählen. … Ich kann ja
 * nicht den Auftrag verplanen, ohne zu sagen: Das machen wir so."*
 *
 * Die Regel steht in `@mrmachine/fach` (`bedienerPflicht`) und nicht hier: Sie
 * gilt auch drüben, und zwei Stellen wären zwei Wahrheiten. Hier steht nur,
 * worauf sie an einem Posten zutrifft — und dass ein Posten ohne bekannte
 * Pflicht keine Meldung auslöst: Solange die Auskunft nicht da war, ist „mit
 * Bediener" eine Frage und keine Zusage.
 */
export function bedienerFehlt(p: Merkposten): boolean {
  return bedienerPflicht(p.bedienungspflicht) && !p.bediener;
}

/**
 * Was sich an einem Posten ändern lässt — und was sich **wegnehmen** lässt.
 *
 * Ein ausdrückliches `undefined` heißt: Das Feld ist weg, nicht „auf
 * undefined gesetzt". Das ist der Weg zurück vom Preis von Hand zum
 * gerechneten: Wer das Feld leert, soll den Tarif wiederbekommen, und ein
 * `einzelpreis: undefined` im Speicher wäre ein Feld, das dasteht und nichts
 * sagt.
 */
export type Teilposten = { [K in keyof Merkposten]?: Merkposten[K] | undefined };

export function postenAendern(id: string, teil: Teilposten): void {
  setzen(jetzt().map((p) => {
    if (p.id !== id) return p;
    const neu: Record<string, unknown> = { ...p, ...teil };
    /* **Ein neuer Zeitraum macht den Betrag alt** (Systemprüfung A-06). Wer
       mit dem Zeitraum zugleich den Betrag bringt, hat schon gerechnet. */
    const anders = (teil.von !== undefined && teil.von !== p.von)
      || (teil.bis !== undefined && teil.bis !== p.bis);
    if (anders && p.gerechnet && istMiete(p) && !("netto" in teil) && !("zeitraumNeu" in teil)) {
      neu["zeitraumNeu"] = true;
    }
    Object.entries(teil).forEach(([schluessel, wert]) => {
      if (wert === undefined) delete neu[schluessel];
    });
    return neu as unknown as Merkposten;
  }));
}

export function postenEntfernen(id: string): void {
  setzen(jetzt().filter((p) => p.id !== id));
}

/**
 * **Nach dem Abschluss geht nur weg, was abgeschickt wurde** (24.09.2026).
 * Was für einen anderen Kunden gemerkt ist, bleibt liegen — der Kunde oben
 * geht mit, denn sein Anruf ist vorbei. Ist danach nichts mehr da, ist das
 * dasselbe wie Leeren.
 */
export function abgeschicktEntfernen(ids: string[]): void {
  const weg = new Set(ids);
  const rest = jetzt().filter((p) => !weg.has(p.id));
  if (!rest.length) { merklisteLeeren(); return; }
  setzen(rest);
  werStand = OHNE_EMPFAENGER;
  try { window.localStorage.removeItem(FACH_WER); } catch { /* wie oben */ }
  beruehrt();
  hoerer.forEach((ruf) => ruf());
}

/**
 * Die Vorlage lösen — **die Sammlung bleibt stehen, nur die Herkunft geht
 * weg.**
 *
 * Wortgleich mit der alten Schnellerfassung (`vorlageLoesen`): Wer den alten
 * Vorgang abgeschüttelt hat, arbeitet ab hier mit den Preisen von heute und
 * will die Marken oben nicht mehr lesen. Was daran von Hand steht — Menge,
 * Satz, Zeitraum, das gewünschte Stück — bleibt; es war seine Entscheidung
 * und nicht die des alten Vorgangs.
 */
export function vorlagenLoesen(): void {
  setzen(jetzt().map(({ vorlage: _weg, ...rest }) => rest));
}

export function merklisteLeeren(): void {
  setzen([]);
  /* **Leeren heißt leeren.** Ein Kunde, der nach dem Wegwerfen der Liste noch
     oben steht, gehört zu einem Anruf, der vorbei ist — und der nächste
     Anrufer bekäme seine Kondition zu sehen. */
  empfaengerSetzen(OHNE_EMPFAENGER);
}

/* ══════════════ Der Abgleich mit dem Server (Migration 0134) ══════════════

   Luca, 18.09.2026 (Durchgang, B9): *„Ja, mach das mal, das ist eine gute
   Idee."* Gemeint war: Die Sammlung gehört an den Menschen. Bis dahin lag
   sie im `localStorage` und damit **je Gerät** — wer am Telefon drei Geräte
   durchrechnet und abends am Schreibtisch weitermachen will, fand dort
   nichts.

   **Der Browser bleibt trotzdem die erste Ablage.** Er antwortet sofort, er
   antwortet ohne Netz, und er antwortet, bevor die Anmeldung steht. Der
   Server ist die zweite, gemeinsame: Was hier geschieht, geht gesammelt
   hinauf; was dort steht, kommt beim Anmelden herunter.

   **Wer gewinnt, entscheidet der Zeitpunkt und nicht die Seite.** Beim
   Anmelden treffen zwei Stände aufeinander. Der neuere gilt — deshalb steht
   in `FACH_STAND`, wann hier zuletzt etwas geschah. Ohne das müsste der
   Abgleich raten, und er würde entweder die Arbeit vom Telefon oder die
   vom Schreibtisch wegwerfen.

   **Ein Fehlschlag ist kein Fehler.** Kein Netz, keine Sitzung, ein Server,
   der hustet: Dann steht die Sammlung eben nur hier, wie vor dem 18.09.2026.
   Eine Preisauskunft, die an einem Abgleich scheitert, wäre schlimmer als
   eine, die ihn nicht schafft. */

/** Wann auf diesem Gerät zuletzt etwas an der Sammlung geschah. */
function standAm(): number {
  try {
    return zahl(window.localStorage.getItem(FACH_STAND), 0);
  } catch {
    return 0;
  }
}

/** Nach jeder Änderung: den Zeitpunkt festhalten und das Hinaufschreiben anmelden. */
function beruehrt(): void {
  try {
    window.localStorage.setItem(FACH_STAND, String(Date.now()));
  } catch {
    /* Wie überall hier: ohne Ablage bleibt es beim Reiter. */
  }
  spaeterHinauf();
}

/** Läuft gerade ein Übernehmen vom Server? Dann nichts zurückschreiben. */
let uebernimmt = false;
/** Steht die Anmeldung, und ist der erste Abgleich gelaufen? */
let angeglichen = false;
let schreibuhr: number | undefined;

/**
 * Gesammelt hinaufschreiben, nicht bei jedem Tastendruck.
 *
 * Wer am Blatt eines Postens die Menge ändert, löst drei Zustände in einer
 * Sekunde aus. Drei `PUT` wären drei Fassungen derselben Sammlung, und die
 * letzte gewinnt ohnehin.
 */
function spaeterHinauf(): void {
  if (!angeglichen || uebernimmt || ATTRAPPE) return;
  window.clearTimeout(schreibuhr);
  schreibuhr = window.setTimeout(() => { void hinauf(); }, 700);
}

/** Was hinaufgeht — die ganze Sammlung, nicht ein Stück davon. */
async function hinauf(): Promise<void> {
  try {
    await ersetze("/merkliste", { wer: werJetzt(), posten: jetzt() });
  } catch {
    /* Beim nächsten Handgriff wieder. Was hier steht, ist nicht verloren. */
  }
}

/** Den Stand vom Server übernehmen, ohne ihn gleich wieder hinaufzuschreiben. */
function uebernehmen(posten: Merkposten[], wer: Merkempfaenger, geaendertAm: number): void {
  uebernimmt = true;
  try {
    setzen(posten);
    empfaengerSetzen(wer);
    try {
      window.localStorage.setItem(FACH_STAND, String(geaendertAm));
    } catch {
      /* siehe oben */
    }
  } finally {
    uebernimmt = false;
  }
}

/** Was die API unter `/merkliste` zurückgibt. */
type Serverstand = {
  wer?: Partial<Merkempfaenger>;
  posten?: unknown[];
  geaendert_am?: string | null;
};

/**
 * **Einmal beim Anmelden**: die Sammlung des Menschen holen und die beiden
 * Stände zusammenbringen. Danach schreibt jede Änderung hinauf.
 *
 * Aufgerufen aus `schale/Sitzung.tsx`, sobald feststeht, wer angemeldet ist —
 * vorher gibt es kein Token und keine Sammlung, die jemandem gehört.
 */
export async function merklisteAngleichen(): Promise<void> {
  if (ATTRAPPE) { angeglichen = false; return; }
  try {
    const roh = await hole<Serverstand>("/merkliste");
    const drueben = Array.isArray(roh.posten)
      ? roh.posten.map(alsPosten).filter((p): p is Merkposten => p !== null)
      : [];
    const werDrueben: Merkempfaenger = {
      nr: typeof roh.wer?.nr === "string" ? roh.wer.nr : "",
      name: typeof roh.wer?.name === "string" ? roh.wer.name : "",
      art: roh.wer?.art === "interessent" || roh.wer?.art === "vermieter" ? roh.wer.art : "kunde",
      ort: typeof roh.wer?.ort === "string" ? roh.wer.ort : "",
      /* Bestellnummer und Hofhinweis führt der Server nicht — sie bleiben,
         was hier steht, solange es derselbe Kunde ist. */
      bestellnummer: "",
      hofhinweis: "",
    };
    if (werDrueben.nr === werJetzt().nr) {
      werDrueben.bestellnummer = werJetzt().bestellnummer;
      werDrueben.hofhinweis = werJetzt().hofhinweis;
    }
    const druebenAm = roh.geaendert_am ? Date.parse(roh.geaendert_am) : 0;
    const hierAm = standAm();
    const hierLeer = jetzt().length === 0 && !werJetzt().nr && !werJetzt().ort;
    const druebenLeer = drueben.length === 0 && !werDrueben.nr && !werDrueben.ort;

    /* **Der neuere Stand gewinnt** — und „leer" ist kein Stand, sondern das
       Fehlen eines. Wer hier nichts hat, bekommt, was drüben steht; wer
       drüben nichts hat, schiebt hinauf, was hier steht (so wandert die
       Sammlung vom 17.09. ohne Zutun an ihren neuen Platz). */
    if (!druebenLeer && (hierLeer || druebenAm > hierAm)) {
      uebernehmen(drueben, werDrueben, Number.isFinite(druebenAm) ? druebenAm : Date.now());
      angeglichen = true;
      return;
    }
    angeglichen = true;
    if (!hierLeer) await hinauf();
  } catch {
    /* Ohne Netz oder ohne Sitzung bleibt es beim Gerät — wie vorher. */
    angeglichen = false;
  }
}

/**
 * **Beim Abmelden**: die Sammlung von diesem Gerät nehmen, drüben stehen
 * lassen.
 *
 * Am selben Rechner meldet sich der Nächste an, und der hat mit dem Anruf
 * von vorhin nichts zu tun. Gelöscht wird **nicht** drüben: Dort gehört sie
 * dem, der sie gesammelt hat, und der findet sie morgen wieder.
 */
export function merklisteAbmelden(): void {
  angeglichen = false;
  window.clearTimeout(schreibuhr);
  uebernimmt = true;
  try {
    setzen([]);
    empfaengerSetzen(OHNE_EMPFAENGER);
    try {
      window.localStorage.removeItem(FACH_STAND);
    } catch {
      /* siehe oben */
    }
  } finally {
    uebernimmt = false;
  }
}

/**
 * Wegwerfen heißt überall wegwerfen — auch drüben.
 *
 * `merklisteLeeren` geht über `setzen([])` ohnehin hinauf und legt dort eine
 * leere Sammlung ab, die die API löscht. Dieser Weg ist der ausdrückliche
 * für den Fall, dass jemand nicht warten soll.
 */
export async function merklisteWegwerfen(): Promise<void> {
  merklisteLeeren();
  if (ATTRAPPE || !angeglichen) return;
  window.clearTimeout(schreibuhr);
  try {
    await loesche("/merkliste");
  } catch {
    /* Dann räumt es der nächste `PUT` mit der leeren Sammlung ab. */
  }
}

/* ══════════════ Der Haken ══════════════ */

/**
 * Die Merkliste, wie eine Maske sie braucht.
 *
 * ```tsx
 * const { posten, summe, merken } = useMerkliste();
 * ```
 *
 * `posten` ist die Liste, `summe` ihr Netto. Die vier Handgriffe sind
 * dieselben Funktionen wie oben — sie stehen hier nur mit, damit eine
 * Maske nur einen Import braucht.
 */
export function useMerkliste() {
  const posten = useSyncExternalStore(anmelden, jetzt, () => LEER);
  const wer = useSyncExternalStore(anmelden, werJetzt, () => OHNE_EMPFAENGER);
  const aendern = useCallback(postenAendern, []);
  const entfernen = useCallback(postenEntfernen, []);
  const leeren = useCallback(merklisteLeeren, []);
  const setzeWer = useCallback(empfaengerSetzen, []);
  return {
    posten,
    /** Für wen der Entwurf ist — Kunde und Einsatzort, beide freiwillig. */
    wer,
    setzeWer,
    summe: merklistenSumme(posten),
    /**
     * Wie viele Stücke insgesamt — die Zahl auf der Leiste.
     *
     * **Nur an einer Miete ist die Menge eine Stückzahl** (Regel 8: jedes
     * Stück wird einzeln disponiert). „4 Stunden Mulchen" sind eine Zeile
     * und nicht vier Posten; sonst stünde am Menüpunkt eine Zahl, die mit
     * der Zahl der Karten nichts zu tun hat.
     */
    stuecke: posten.reduce((s, p) => s + (istMiete(p) ? p.menge : 1), 0),
    /** Wie viele Zeilen noch keinen Betrag haben. */
    offene: posten.filter((p) => !p.gerechnet).length,
    /** Die Posten, die zwingend einen Bediener brauchen und keinen tragen. */
    ohneBediener: posten.filter(bedienerFehlt),
    /**
     * Woraus die Sammlung wiederholt ist — und die beiden Marken darüber.
     *
     * `nummern` ist leer, wenn nichts wiederholt wurde; dann steht das Band
     * über den Karten gar nicht (`bausteine/Merkliste.tsx`, `Vorlagenband`).
     */
    vorlagen: vorlagenstand(posten),
    merken,
    /** Merken, wenn es frei ist — sonst das Fenster (19.09.2026). */
    merkenWennFrei,
    /** Menge und Zeitraum ändern, wenn es dann noch frei ist (19.09.2026). */
    aendernWennFrei: postenAendernWennFrei,
    aendern,
    entfernen,
    leeren,
    vorlagenLoesen,
  };
}
